/**
 * Страница РЕЗУЛЬТАТА оплаты (TASK-061, переработана в KAN-343).
 *
 * ⭐ Шлюз возвращает человека на один и тот же адрес при любом исходе — и при
 * успехе, и при отказе. Значит «что случилось» кабинет обязан спросить сам.
 * Раньше страница безусловно рисовала «Оплата прошла успешно»: после отказа
 * по деньгам она врала клиенту в лицо, а тариф при этом не подключался.
 *
 * Поэтому здесь читается ФАКТИЧЕСКИЙ статус платежа, и только он решает, что
 * показать. Причина отказа приходит готовым текстом с бэкенда: разбирать коды
 * шлюза в трёх местах (история, эта страница, письма) — значит завести три
 * словаря, которые разойдутся.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { billingApi } from '@/services/api/billing';
import { subscriptionApi } from '@/services/api/subscriptions';
import type { Payment } from '@/types/billing';
import type { OrganizationSubscription } from '@/types/subscription';

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const paymentId = params.get('payment');

  useEffect(() => {
    document.title = 'Результат оплаты — SMETA AI';
  }, []);

  const { data: payment, isLoading } = useQuery<Payment>({
    queryKey: ['payment-result', paymentId],
    queryFn: () => billingApi.getPayment(paymentId as string),
    enabled: Boolean(paymentId),
  });

  const { data: subscription } = useQuery<OrganizationSubscription | null>({
    queryKey: ['current-subscription'],
    queryFn: subscriptionApi.getCurrent,
  });

  if (paymentId && isLoading) {
    return (
      <div data-testid="payment-result-loading" className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  // Статус приходит от шлюза строчными; сравнение регистронезависимое.
  const status = (payment?.status || '').toLowerCase();
  const succeeded = status === 'succeeded';
  const canceled = status === 'canceled';

  return (
    <div
      data-testid="payment-result-page"
      className="flex min-h-[400px] flex-col items-center justify-center py-12">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6 text-center">
          {succeeded ? (
            <>
              <CheckCircle
                data-testid="result-icon-success"
                className="mx-auto mb-4 h-16 w-16 text-green-500"
              />
              <h1 className="mb-2 text-2xl font-bold text-gray-900">Оплата прошла</h1>
              <p data-testid="payment-result-message" role="status" className="mb-6 text-gray-500">
                Тариф подключён, работа в кабинете доступна.
              </p>

              {subscription && (
                <div className="mb-6 space-y-3 rounded-lg bg-gray-50 p-4 text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Тариф</span>
                    <span data-testid="activated-plan-name" className="font-medium">
                      {subscription.plan.name}
                    </span>
                  </div>
                  {subscription.ends_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Действует до</span>
                      <span data-testid="activated-plan-until" className="text-sm tabular-nums">
                        {formatDate(subscription.ends_at)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : canceled ? (
            <>
              <XCircle
                data-testid="result-icon-declined"
                className="mx-auto mb-4 h-16 w-16 text-red-500"
              />
              <h1 className="mb-2 text-2xl font-bold text-gray-900">Платёж не прошёл</h1>
              <p data-testid="payment-result-message" role="status" className="mb-2 text-gray-600">
                {payment?.cancellation_reason || 'Платёж не прошёл'}
              </p>
              <p className="mb-6 text-sm text-gray-500">
                Тариф не подключён, деньги не списаны. Можно попробовать ещё раз.
              </p>
            </>
          ) : (
            <>
              <Clock data-testid="result-icon-pending" className="mx-auto mb-4 h-16 w-16 text-amber-500" />
              <h1 className="mb-2 text-2xl font-bold text-gray-900">Платёж в обработке</h1>
              <p data-testid="payment-result-message" role="status" className="mb-6 text-gray-500">
                {/* Ожидание — нормальный исход, а не ошибка: шлюз может
                    подтвердить оплату позже, и придумывать здесь провал
                    значило бы пугать клиента раньше времени. */}
                Счёт ещё не оплачен. Как только шлюз подтвердит оплату, тариф подключится
                автоматически.
              </p>
            </>
          )}

          <div className="flex flex-col gap-3">
            <Button data-testid="go-to-billing-button" asChild className="w-full">
              <Link to="/billing">{succeeded ? 'Перейти к тарифу' : 'Вернуться к тарифам'}</Link>
            </Button>
            <Button data-testid="go-to-dashboard-button" variant="outline" asChild className="w-full">
              <Link to="/">На главную</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
